import type { DeviceTimeseriesKey } from "@/base/sensors"
import type { MinDateMaxDate } from "@/base/types"
import type { EntityState, Reducer } from "@reduxjs/toolkit"

import * as AppStorage from "@/base/async-storage"
import {
  callNamedDeviceActionAsync,
  loadDeviceFlowHistoryDataAsync,
  loadDeviceFunctionCallsAsync,
  loadDeviceTimeseriesDataAsync,
  requestUpdateFromDeviceAsync,
} from "@/base/farmhq-api"
import {
  convertSensorValue,
  requiresConversion,
} from "@/base/sensor-conversions"
import { isNumberValid, isTruthyString } from "@/base/type-guards"
import * as Models from "@/redux/models"
import { createEntityAdapter, createSlice, isAnyOf } from "@reduxjs/toolkit"

import type { RootState } from "./root.reducer"

import { useDeviceProfileContext } from "@/components/DeviceProfileContext"
import { createCachedSelector } from "re-reselect"
import { signOutAsync } from "./auth.reducer"
import {
  getDeviceAnalyticsDateValue,
  getUserMeasurementPreferenceFromState,
} from "./selectors"
import { useRootSelector } from "./useRootSelector"

export const functionInvocationsAdapter =
  createEntityAdapter<Models.DeviceFunctionCall>({
    sortComparer: (a, b) => {
      if (
        isTruthyString(a.requestSentTimestamp) &&
        isTruthyString(b.requestSentTimestamp)
      ) {
        return b.requestSentTimestamp.localeCompare(a.requestSentTimestamp)
      }
      return 0
    },
  })

export const { selectAll: selectAllDeviceFunctionCalls } =
  functionInvocationsAdapter.getSelectors(
    (state: RootState) => state.deviceProfile.functionInvocations,
  )

interface DeviceProfileState {
  analytics: {
    dateMsMax: number
    dateMsMin: number
  }
  functionInvocations: EntityState<Models.DeviceFunctionCall, number>
  reelRuns: Models.ModelState<Models.ReelRun>
  timeseries: Models.ModelState<
    Models.DeviceTimeSeriesUnion,
    Models.DeviceTimeSeriesUnion["key"]
  >
}

const initialState: DeviceProfileState = {
  analytics: {
    dateMsMax: AppStorage.STORAGE_DEFAULTS.deviceAnalyticsDateMax,
    dateMsMin: AppStorage.STORAGE_DEFAULTS.deviceAnalyticsDateMin,
  },
  functionInvocations: functionInvocationsAdapter.getInitialState(),
  reelRuns: { ...Models.getInitialEntityState() },
  timeseries: { ...Models.getInitialEntityState() },
}

const slice = createSlice({
  extraReducers: (builder) =>
    builder
      .addCase(
        loadDeviceFunctionCallsAsync.fulfilled,
        (state, { payload }) => {
          functionInvocationsAdapter.setAll(
            state.functionInvocations,
            payload.items,
          )
        },
      )
      .addCase(
        AppStorage.readLocalStorageAsync.fulfilled,
        (state, { payload }) => {
          state.analytics.dateMsMin = payload.deviceAnalyticsDateMin
          state.analytics.dateMsMax = payload.deviceAnalyticsDateMax
        },
      )
      .addCase(
        loadDeviceTimeseriesDataAsync.fulfilled,
        (state, { payload }) => {
          Models.deviceTimeseries.adapter.upsertMany(
            state.timeseries,
            payload.items,
          )
        },
      )
      .addCase(
        loadDeviceFlowHistoryDataAsync.fulfilled,
        (state, { payload }) => {
          Models.deviceTimeseries.adapter.upsertMany(
            state.timeseries,
            payload.items,
          )
        },
      )
      .addCase(
        // MIN DATE
        AppStorage.setDeviceAnalyticsDateMin.pending,
        (state, action) => {
          state.analytics.dateMsMin = action.meta.arg
        },
      )
      .addCase(
        // MAX DATE
        AppStorage.setDeviceAnalyticsDateMax.pending,
        (state, action) => {
          state.analytics.dateMsMax = action.meta.arg
        },
      )
      .addCase(callNamedDeviceActionAsync.fulfilled, (state, action) => {
        if (action.payload.result) {
          functionInvocationsAdapter.upsertOne(
            state.functionInvocations,
            action.payload.result,
          )
        }
      })
      .addMatcher(
        isAnyOf(
          callNamedDeviceActionAsync.fulfilled,
          requestUpdateFromDeviceAsync.fulfilled,
        ),
        (state, { payload }) => {
          if (payload.result) {
            functionInvocationsAdapter.upsertOne(
              state.functionInvocations,
              payload.result,
            )
          }
        },
      )
      .addMatcher(isAnyOf(signOutAsync.fulfilled), () => ({
        ...initialState,
      })),
  initialState,
  name: "deviceProfile",
  reducers: {},
})

const deviceProfile: Reducer<DeviceProfileState> = slice.reducer

export default deviceProfile
export function useDeviceProfileDateValues(): MinDateMaxDate {
  return {
    dateMsMax: useRootSelector(getDeviceAnalyticsDateValue.getMax),
    dateMsMin: useRootSelector(getDeviceAnalyticsDateValue.getMin),
  }
}

export const getTimeseriesValuesFromState = createCachedSelector(
  [
    getUserMeasurementPreferenceFromState,
    (_state: RootState, key: DeviceTimeseriesKey) =>
      Models.deviceTimeseries.selectById(_state, key),
    (_state: RootState, _key: DeviceTimeseriesKey, deviceId: string) =>
      deviceId,
  ],
  (measurementPreference, data) => {
    if (data) {
      if (requiresConversion(data.key)) {
        const convert = (rawValue: number): number => {
          return convertSensorValue({
            fieldName: data.key,
            measurementPreference,
            rawValue,
            target: "user",
          })
        }

        return {
          ...data,
          maxValue: convert(data.maxValue),
          values: data.values.map((value) => {
            if (isNumberValid(value.y)) {
              return { ...value, y: convert(value.y) }
            }
            return value
          }),
        }
      }
    }
    return data
  },
)((_state, key, deviceId) => `${key}/${deviceId}`)

export function useTimeseriesValuesFromState(
  key: DeviceTimeseriesKey,
): Models.DeviceTimeSeriesUnion | undefined {
  const deviceId = useDeviceProfileContext().deviceId
  const graphData = useRootSelector((state) =>
    getTimeseriesValuesFromState(state, key, deviceId),
  )
  return graphData
}
